import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import { getUserProfile, sendFriendRequest } from "../../api/communityApi";

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await getUserProfile(userId);
        setProfile(data);
      } catch (err) {
        setError("Could not load this profile.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  const handleAddFriend = async () => {
    try {
      await sendFriendRequest(profile.id);
      setMessage("Friend request sent!");
      setProfile((prev) => ({ ...prev, friendship_status: "pending", friendship_direction: "sent" }));
      setTimeout(() => setMessage(""), 3000);
    } catch {
      setMessage("Failed to send request.");
      setTimeout(() => setMessage(""), 3000);
    }
  };

  const displayName = profile
    ? profile.first_name
      ? `${profile.first_name} ${profile.last_name}`
      : profile.username
    : "";

  const avatarUrl = profile?.avatar_url
    || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=4f46e5&color=fff&size=128`;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gradient-to-b dark:from-gray-900 dark:via-gray-950 dark:to-black dark:text-white">
      <Navbar />

      <div className="max-w-3xl mx-auto pt-28 px-6 pb-20 animate-fadeIn">

        {/* Back button */}
        <button
          onClick={() => navigate("/community")}
          className="mb-8 flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400 transition text-sm"
        >
          ← Back to Community
        </button>

        {loading && (
          <div className="text-center text-gray-500 dark:text-gray-400 mt-20">Loading profile...</div>
        )}

        {error && (
          <div className="text-center text-red-400 mt-20">{error}</div>
        )}

        {!loading && !error && profile && (
          <>
            {/* Profile card */}
            <div className="p-8 rounded-2xl shadow-lg bg-white border border-gray-200 dark:bg-gray-800/60 dark:border-gray-700 mb-6">
              {/* Avatar + Name */}
              <div className="flex items-center gap-6 mb-8">
                <img
                  src={avatarUrl}
                  alt={displayName}
                  className="w-24 h-24 rounded-full object-cover border-4 border-indigo-500 dark:border-indigo-400 shadow-lg"
                />
                <div>
                  <h1 className="text-3xl font-bold">{displayName}</h1>
                  <p className="text-gray-500 dark:text-gray-400 mt-1">@{profile.username}</p>

                  {/* 30-Day Return Badge */}
                  {profile.thirty_day_return !== null && profile.thirty_day_return !== undefined && (
                    <span
                      className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-semibold ${
                        profile.thirty_day_return >= 0
                          ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"
                          : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400"
                      }`}
                    >
                      {profile.thirty_day_return >= 0 ? "+" : ""}{profile.thirty_day_return}% (30d)
                    </span>
                  )}
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="p-4 bg-gray-100 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Friends</p>
                  <p className="text-2xl font-bold">{profile.friends_count ?? "—"}</p>
                </div>
                <div className="p-4 bg-gray-100 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Holdings</p>
                  <p className="text-2xl font-bold">{profile.holdings_count ?? "—"}</p>
                </div>
              </div>

              {/* Action button */}
              {message && (
                <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg text-green-700 dark:text-green-400 text-sm">
                  {message}
                </div>
              )}

              {profile.friendship_status === "accepted" ? (
                <div className="w-full py-3 bg-gray-100 dark:bg-gray-700/50 rounded-xl text-center text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 font-medium">
                  ✅ You are friends
                </div>
              ) : profile.friendship_status === "pending" ? (
                <div className="w-full py-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl text-center text-indigo-600 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 font-medium">
                  {profile.friendship_direction === "sent" ? "⏳ Request Sent" : "📬 They sent you a request — check Friend Requests"}
                </div>
              ) : (
                <button
                  onClick={handleAddFriend}
                  className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-white font-semibold transition shadow hover:shadow-indigo-500/30"
                >
                  ➕ Add Friend
                </button>
              )}
            </div>

            {/* Privacy note */}
            <p className="text-center text-xs text-gray-400 dark:text-gray-600">
              🔒 Detailed portfolio data is private. Only public stats are shown here.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
